import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { AppRole } from '@/hooks/useUserRole';

interface RoleTestContextType {
  testingAsRole: AppRole | null;
  setTestingAsRole: (role: AppRole | null) => void;
  restoreOriginalRole: () => void;
  isTestingRole: boolean;
}

const RoleTestContext = createContext<RoleTestContextType | undefined>(undefined);

const STORAGE_KEY = 'role_test_simulation';

export function RoleTestProvider({ children }: { children: ReactNode }) {
  const [testingAsRole, setTestingAsRoleState] = useState<AppRole | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored as AppRole | null;
  });

  const setTestingAsRole = (role: AppRole | null) => {
    setTestingAsRoleState(role);
    if (role) {
      localStorage.setItem(STORAGE_KEY, role);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const restoreOriginalRole = () => {
    setTestingAsRole(null);
  };

  // Sync with localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setTestingAsRoleState(stored as AppRole);
    }
  }, []);

  return (
    <RoleTestContext.Provider
      value={{
        testingAsRole,
        setTestingAsRole,
        restoreOriginalRole,
        isTestingRole: testingAsRole !== null,
      }}
    >
      {children}
    </RoleTestContext.Provider>
  );
}

export function useRoleTest() {
  const context = useContext(RoleTestContext);
  if (context === undefined) {
    throw new Error('useRoleTest must be used within a RoleTestProvider');
  }
  return context;
}

// Safe hook that doesn't throw if used outside provider
export function useRoleTestSafe() {
  const context = useContext(RoleTestContext);
  return context ?? {
    testingAsRole: null,
    setTestingAsRole: () => {},
    restoreOriginalRole: () => {},
    isTestingRole: false,
  };
}
